import React, { useEffect, useState } from "react";
import { Phone, Mail, MapPin, Briefcase, Send } from "lucide-react";
import { useLang } from "@/i18n";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

const inputCls = "w-full rounded-xl border border-border bg-cream-card px-4 py-3 text-sm outline-none transition focus:border-gold";

export default function Contact() {
  const { lang, t } = useLang();
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [done, setDone] = useState(false);
  useEffect(() => { api.get("/content/settings").then((r) => setSettings(r.data)).catch(() => {}); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/contact", form); setDone(true); toast.success(lang === "fr" ? "Message envoyé !" : "Message sent!"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail, lang)); }
  };

  const info = [
    { icon: Phone, label: t("common.phone"), value: settings?.phone },
    { icon: Mail, label: lang === "fr" ? "Support client" : "Customer Support", value: settings?.support_email },
    { icon: Briefcase, label: lang === "fr" ? "Demandes business" : "Business Inquiry", value: settings?.business_email },
    { icon: MapPin, label: lang === "fr" ? "Marché" : "Market", value: settings?.address },
  ];

  return (
    <div data-testid="contact-page">
      <section className="bg-navy py-14">
        <div className="mx-auto max-w-[1400px] px-4 text-center">
          <h1 className="font-display text-4xl font-bold text-cream sm:text-5xl">{t("nav.contact")}</h1>
          <p className="mx-auto mt-3 max-w-lg text-cream/80">{lang === "fr" ? "Une question ? Notre équipe est à votre écoute." : "A question? Our team is here to help."}</p>
        </div>
      </section>

      <section className="mx-auto grid max-w-[1400px] gap-10 px-4 py-16 lg:grid-cols-2">
        <div>
          <h2 className="mb-6 font-display text-2xl font-bold text-navy">{lang === "fr" ? "Nos coordonnées" : "Get in touch"}</h2>
          <div className="space-y-4">
            {info.map((i, idx) => (
              <div key={idx} className="flex items-center gap-4 rounded-2xl border border-border bg-cream-card p-5 card-shadow">
                <div className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-gold/15 text-gold"><i.icon size={22} /></div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{i.label}</p>
                  <p className="font-head font-semibold text-navy">{i.value || "—"}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-3xl border border-border bg-cream-card p-8 card-shadow-lg">
          <h2 className="mb-5 font-display text-2xl font-bold text-navy">{lang === "fr" ? "Envoyez-nous un message" : "Send us a message"}</h2>
          {done ? (
            <div className="rounded-2xl bg-palm-light/50 p-6 text-center" data-testid="contact-success">
              <p className="font-head text-lg font-semibold text-palm">{lang === "fr" ? "Merci ! Nous vous répondrons bientôt." : "Thank you! We'll respond soon."}</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
              <input required placeholder={t("common.name")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="contact-name" />
              <input required type="email" placeholder={t("common.email")} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} data-testid="contact-email" />
              <input placeholder={lang === "fr" ? "Sujet" : "Subject"} value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className={inputCls} data-testid="contact-subject" />
              <textarea required rows={5} placeholder={lang === "fr" ? "Votre message..." : "Your message..."} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className={inputCls} data-testid="contact-message" />
              <button type="submit" className="btn-gold flex w-full items-center justify-center gap-2 rounded-full py-3.5 text-sm font-bold" data-testid="contact-submit"><Send size={16} /> {t("common.submit")}</button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
